// Apollo
import { useMutation } from '@apollo/client';
// Types
import {
  FollowStores,
  FollowStoresVariables,
} from 'api/follow/types/FollowStores';
import {
  UnfollowStores,
  UnfollowStoresVariables,
} from 'api/follow/types/UnfollowStores';
import { GetTopCreatorStores_stores_entities } from 'api/store/types/GetTopCreatorStores';
// Mutations
import { FOLLOW_STORE, UNFOLLOW_STORE } from 'api/follow/mutations';
// Queries
import { GET_TOP_CREATOR_STORES } from 'api/store/queries';
// Hooks
import { useAppContext, useGetCurrUser } from 'hooks';
// UI Components
import Button from 'ui3/Button/Button';
import LinkButton from 'ui3/LinkButton/LinkButton';
// Components
import { showToast } from 'components/common/Toast/Toast';
// Styles
import styles from './CreatorCard.module.scss';

type CreatorCardProps = {
  store: GetTopCreatorStores_stores_entities;
};

const CreatorCard = ({ store }: CreatorCardProps) => {
  const avatarUrl = store.storeDetails?.avatarURL || '';
  const storeName = store.storeDetails?.storeName || '';
  const sport = store.sports?.[0]?.name || '';
  const slug = store.slug || '';

  const { data: user } = useGetCurrUser();
  const isLoggedIn = Boolean(user?.me?.id);
  const { setLoginModalVisibility } = useAppContext();

  const isFollowing = Boolean(store.isFollowing);

  const [followStore, { loading: followMutating }] = useMutation<
    FollowStores,
    FollowStoresVariables
  >(FOLLOW_STORE);

  const [unfollowStore, { loading: unfollowMutating }] = useMutation<
    UnfollowStores,
    UnfollowStoresVariables
  >(UNFOLLOW_STORE);

  const handleFollowStore = async () => {
    if (!isLoggedIn) {
      setLoginModalVisibility(true);
      return;
    }

    try {
      await followStore({
        variables: {
          input: {
            storeIds: [store.id],
          },
        },
        refetchQueries: [
          {
            query: GET_TOP_CREATOR_STORES,
          },
        ],
      });
    } catch (error) {
      showToast({
        message: (error as Error).message || 'Error following the store',
        type: 'error',
      });
    }
  };

  const handleUnfollowStore = async () => {
    if (!isLoggedIn) {
      setLoginModalVisibility(true);
      return;
    }

    try {
      await unfollowStore({
        variables: {
          input: {
            storeIds: [store.id],
          },
        },
        refetchQueries: [
          {
            query: GET_TOP_CREATOR_STORES,
          },
        ],
      });
    } catch (error) {
      showToast({
        message: (error as Error).message || 'Error unfollowing the store',
        type: 'error',
      });
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.imageWrapper}>
        <img
          src={avatarUrl}
          alt={storeName}
          title={storeName ? `${storeName} on MILLIONS` : undefined}
          className={styles.profileImage}
        />
      </div>
      <div className={styles.content}>
        {sport && <div className={styles.sportChip}>{sport}</div>}
        <div className={styles.storeName}>{storeName}</div>
        <div className={styles.buttons}>
          {!isFollowing && (
            <Button
              loading={followMutating}
              variant={'primary'}
              className={styles.followButton}
              onClick={handleFollowStore}
              size="medium"
              icon="plus"
            >
              Follow
            </Button>
          )}
          {isFollowing && (
            <Button
              loading={unfollowMutating}
              variant={'secondary'}
              className={styles.unfollowButton}
              onClick={handleUnfollowStore}
              size="medium"
              icon="check"
            >
              Following
            </Button>
          )}
          <LinkButton
            variant="secondary"
            subVariant="outlined"
            size="medium"
            link={`/${slug}`}
            className={styles.viewButton}
          >
            View
          </LinkButton>
        </div>
      </div>
    </div>
  );
};

export default CreatorCard;
